import { Check, ChevronDown, RotateCcw } from 'lucide-react';
import { PLAIN_STATUS_DOT, plainStatus } from '../../lib/plain-status';
import { cn } from '../../lib/utils';
import type { StateDto } from '../../lib/work-types';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import StatusPill from './StatusPill';

function firstOf(states: StateDto[], groups: string[]): StateDto | undefined {
  for (const group of groups) {
    const match = states.find((state) => state.group === group && state.is_default) ?? states.find((state) => state.group === group);
    if (match) return match;
  }
  return undefined;
}

/** Where "Mark done" lands: the stream's default finished column, else one called Done, else its last. */
export function finishState(states: StateDto[]): StateDto | undefined {
  const finished = states.filter((state) => state.group === 'completed');
  return (
    finished.find((state) => state.is_default) ??
    finished.find((state) => state.name.trim().toLowerCase() === 'done') ??
    finished[finished.length - 1]
  );
}

/** The task's status you can click to change, with one obvious way to finish it or reopen it. */
export default function StatusControl({
  stateId,
  states,
  blocked,
  readOnly,
  compact = false,
  markedDone,
  onChange,
}: {
  stateId: string;
  states: StateDto[];
  blocked: boolean;
  readOnly: boolean;
  /** A small status menu for list rows, without the Mark done and Reopen buttons. */
  compact?: boolean;
  /** Pass whether the task was marked done; a Done column alone then reads as in progress, and Reopen is left to the caller. */
  markedDone?: boolean;
  onChange: (stateId: string) => void;
}) {
  const state = states.find((row) => row.id === stateId);
  if (!state) return null;
  const done = state.group === 'completed';
  const reopenState = firstOf(states, ['started', 'unstarted', 'backlog']);
  const pillStatus = markedDone === undefined ? undefined : markedDone ? 'done' : done ? 'in_progress' : undefined;

  if (readOnly) return <StatusPill state={state} status={pillStatus} blocked={blocked} size={compact ? 'sm' : 'md'} showStep={!compact} />;

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-shortcut="state"
            aria-label={`Status: ${blocked ? 'Blocked' : state.name}. Change status`}
            className="inline-flex items-center gap-1 rounded-full pr-1.5 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <StatusPill state={state} status={pillStatus} blocked={blocked} size={compact ? 'sm' : 'md'} showStep={!compact} />
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[14rem]">
          {states.map((row) => (
            <DropdownMenuItem key={row.id} onSelect={() => onChange(row.id)} className="justify-between gap-3">
              <span className="inline-flex items-center gap-2">
                <span
                  className={cn('h-2 w-2 shrink-0 rounded-full', PLAIN_STATUS_DOT[plainStatus({ group: row.group, name: row.name })])}
                  aria-hidden
                />
                {row.name}
              </span>
              {row.id === stateId ? <Check className="h-3.5 w-3.5 text-primary" aria-label="Current" /> : null}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {!compact && markedDone === undefined && done && reopenState ? (
        <Button type="button" size="sm" variant="outline" onClick={() => onChange(reopenState.id)}>
          <RotateCcw className="h-3.5 w-3.5" aria-hidden />
          Reopen
        </Button>
      ) : null}
    </span>
  );
}
